import { ReactNode, useEffect, useMemo, useState } from 'react';
import { JSONContent } from '@tiptap/core';

import {
  ActivitiesContentType,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import type { LessonActivitiesFragment } from '../lesson-builder.graphql.generated';
import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import {
  getClosestImageUrl,
  ImageDisplayQuality,
} from 'approot/shared/images/imgix-responsive';
import ActivityBuilderIntroductionView from '../introduction/activity-builder-intro-view';
import ComponentsPreview from '../components/components-preview';
import { getSectionsAndSubSections } from '../activity-builder.utils';
import { useGetActivitySections } from '../activity-builder-section.hooks';
import {
  usePagePreviewIntersectionObserver,
  usePagePreviewResizeObserver,
} from './activity-builder-page-preview.hooks';
import { PagePreviewContent } from './activity-builder-page-preview-shared';
import {
  ActivityBuilderIntroFooter,
  ActivityBuilderIntroHeader,
} from '../introduction/activity-builder-intro';
import { getContentHeight } from './activity-builder-page-preview.utils';
import ActivityPrint from '../components/activity/activity-print';
import { useReadSectionFromCache } from '../present/activity-present.hooks';
import type { BookmarkedSectionOverviewFragment } from '../../shared/bookmarks/bookmark.graphql.generated';
import type { InteractiveLessonSectionOverviewFragment } from '../lesson-builder.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

export type HeaderFooterContentBase = {
  unitName: string;
  contentName: string;
  subjectName: string;
};

type HeaderFooterContent = HeaderFooterContentBase & {
  pageNumber: number;
  hideCheckPoint?: boolean;
};

export const ActivityBuilderIntroPreview = ({
  focusQuestion,
  phenomena,
  assessmentName,
  introBackground,
  headerFooterContent,
  images,
  authorId,
  includeDifferentiation = true,
  language = LANGUAGE_CODE_EN_AU,
}: {
  focusQuestion?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  phenomena?: Pluck<LessonActivitiesFragment, 'phenomena'> | null;
  assessmentName?: JSONContent | null;
  introBackground?: string | null;
  headerFooterContent?: HeaderFooterContent;
  images?: ResponsiveImage[];
  authorId?: number;
  includeDifferentiation?: boolean;
  language?: LanguageCodes;
}) => {
  const [bgImage, setBgImage] = useState<string | null | undefined>(
    introBackground
  );
  const { containerRef, setContainerRef } = usePagePreviewResizeObserver();

  const { pageHeight, contentHeight } = useMemo(
    () =>
      getContentHeight({
        showHeaders: !!headerFooterContent,
        includeDifferentiation,
      }),
    [headerFooterContent, includeDifferentiation]
  );

  useEffect(() => {
    if (!introBackground && containerRef?.offsetWidth) {
      const imageUrl = getClosestImageUrl({
        containerSize: containerRef.offsetWidth,
        imageUrls: images || [],
        quality: ImageDisplayQuality.High,
      });
      setBgImage(imageUrl);
    }
  }, [introBackground, images, containerRef?.offsetWidth]);

  useEffect(() => {
    if (introBackground) {
      setBgImage(introBackground);
    }
  }, [introBackground]);

  return (
    <PagePreviewContent
      ref={setContainerRef}
      isLoading={false}
      dimensions={{ pageHeight, contentHeight }}
      bgUrl={bgImage || ''}
      header={
        headerFooterContent && (
          <ActivityBuilderIntroHeader
            unitName={headerFooterContent.unitName}
            contentName={headerFooterContent.contentName}
          />
        )
      }
      footer={
        headerFooterContent && (
          <ActivityBuilderIntroFooter
            pageNumber={headerFooterContent.pageNumber}
            subject={headerFooterContent.subjectName}
            showGoogleAttribution={language !== LANGUAGE_CODE_EN_AU}
          />
        )
      }
    >
      <ActivityBuilderIntroductionView
        focusQuestion={focusQuestion || null}
        phenomena={phenomena || null}
        assessmentName={assessmentName}
        authorId={authorId}
      />
    </PagePreviewContent>
  );
};

export const ActivityBuilderPagePreview = ({
  id,
  contentId,
  contentType,
  classId,
  canFetch = true,
  parentElement,
  initialVisibility,
  version,
  language = LANGUAGE_CODE_EN_AU,
  sectionOverviews,
  headerFooterContent,
  isPublic,
}: {
  id: string;
  contentId: number;
  contentType: ActivitiesContentType;
  classId: number | null;
  canFetch?: boolean;
  parentElement?: HTMLElement | null;
  initialVisibility?: boolean;
  version?: string | null;
  language?: LanguageCodes;
  sectionOverviews?:
    | InteractiveLessonSectionOverviewFragment[]
    | BookmarkedSectionOverviewFragment[];
  headerFooterContent?: HeaderFooterContent;
  isPublic?: boolean;
}) => {
  const { containerRef, setContainerRef } = usePagePreviewResizeObserver();

  const { isVisible } = usePagePreviewIntersectionObserver({
    containerElement: containerRef,
    initialVisibility,
    parentElement,
  });

  const sectionFromCache = useReadSectionFromCache({
    sectionId: id,
    skip: !version,
    language,
    version,
  });

  const { data, loading } = useGetActivitySections({
    contentId,
    contentType,
    classId,
    sectionIds: [id],
    skip: !!sectionFromCache || !canFetch || !isVisible,
    version,
    language,
    isPublic,
  });
  const section = useMemo(() => {
    if (sectionFromCache) return sectionFromCache;
    return (
      data?.getActivitiesSections &&
      getSectionsAndSubSections(data.getActivitiesSections).find(
        s => s.id === id
      )
    );
  }, [data?.getActivitiesSections, id, sectionFromCache]);

  const { pageHeight, contentHeight } = useMemo(
    () =>
      getContentHeight({
        showHeaders: !!headerFooterContent,
        includeDifferentiation: !!sectionOverviews,
      }),
    [headerFooterContent, sectionOverviews]
  );

  return (
    <PagePreviewContent
      ref={setContainerRef}
      dimensions={{ pageHeight, contentHeight }}
      isLoading={loading || !section}
      header={
        headerFooterContent && (
          <ActivityBuilderIntroHeader
            unitName={headerFooterContent.unitName}
            contentName={headerFooterContent.contentName}
          />
        )
      }
      footer={
        headerFooterContent && (
          <ActivityBuilderIntroFooter
            pageNumber={headerFooterContent.pageNumber}
            subject={headerFooterContent.subjectName}
            showGoogleAttribution={language !== LANGUAGE_CODE_EN_AU}
          />
        )
      }
    >
      {section && sectionOverviews && (
        <ActivityPrint
          section={section}
          sectionsOverview={sectionOverviews}
          hideCheckpoint={!!headerFooterContent?.hideCheckPoint}
        />
      )}
      {section && <ComponentsPreview section={section} contentId={contentId} />}
    </PagePreviewContent>
  );
};

export const ActivityPagePreview = ({ children }: { children: ReactNode }) => {
  const { setContainerRef } = usePagePreviewResizeObserver();

  const { pageHeight, contentHeight } = useMemo(
    () =>
      getContentHeight({
        showHeaders: true,
        includeDifferentiation: true,
      }),
    []
  );

  return (
    <PagePreviewContent
      ref={setContainerRef}
      isLoading={false}
      dimensions={{ pageHeight, contentHeight }}
    >
      {children}
    </PagePreviewContent>
  );
};
